<template>
  <Teleport to="body">
    <transition name="socket-banner">
      <div
        v-if="showBanner"
        class="socket-banner"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        <div class="socket-banner__icon-wrap" aria-hidden="true">
          <Icon icon="mdi:wifi-off" width="16" height="16" class="socket-banner__icon" />
          <span class="socket-banner__pulse" />
        </div>
        <div class="socket-banner__copy">
          <span class="socket-banner__title">
            Reconnecting<span class="socket-banner__ellipsis" aria-hidden="true">
              <span></span><span></span><span></span>
            </span>
          </span>
          <span class="socket-banner__subtitle">Live captions paused</span>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from "vue";
import { socketConnected } from "@/utils/socket-rooms";
import { useIncomingCall } from "@/composables/calls/useIncomingCall";
import { Icon } from "@/components/ui";

// Avoid flashing the banner on sub-second network blips (Wi-Fi handoff, sleep/wake).
const SHOW_DELAY_MS = 800;

const { parentCallSid } = useIncomingCall();
const isCallInFlight = computed(() => parentCallSid.value !== null);

const graceElapsed = ref(false);
let showTimer: ReturnType<typeof setTimeout> | null = null;

const clearShowTimer = () => {
  if (showTimer) {
    clearTimeout(showTimer);
    showTimer = null;
  }
};

watch(
  [socketConnected, isCallInFlight],
  ([connected, inCall]) => {
    if (connected || !inCall) {
      clearShowTimer();
      graceElapsed.value = false;
      return;
    }
    if (graceElapsed.value || showTimer) return;
    showTimer = setTimeout(() => {
      graceElapsed.value = true;
      showTimer = null;
    }, SHOW_DELAY_MS);
  },
  { immediate: true }
);

const showBanner = computed(
  () => !socketConnected.value && isCallInFlight.value && graceElapsed.value
);

onBeforeUnmount(clearShowTimer);
</script>

<style scoped>
.socket-banner {
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 20001;
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 280px;
  max-width: 360px;
  padding: 12px 16px;
  background: linear-gradient(to right, #fffbeb 0%, #ffffff 12%);
  color: var(--color-text, #282a30);
  font-family: var(--font-family, "Inter", sans-serif);
  font-size: var(--font-size-sm, 0.82rem);
  border-radius: 12px;
  border-left: 4px solid var(--color-warning, #f59e0b);
  box-shadow:
    0 10px 25px -5px rgba(0, 0, 0, 0.1),
    0 8px 10px -6px rgba(0, 0, 0, 0.1),
    0 0 0 1px rgba(0, 0, 0, 0.05);
  backdrop-filter: blur(8px);
  pointer-events: none;
}

.socket-banner__icon-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border-radius: 999px;
  background: rgba(245, 158, 11, 0.12);
  color: var(--color-warning, #f59e0b);
}

.socket-banner__icon {
  position: relative;
  z-index: 1;
}

.socket-banner__pulse {
  position: absolute;
  inset: 0;
  border-radius: inherit;
  border: 2px solid var(--color-warning, #f59e0b);
  opacity: 0.5;
  animation: socket-banner-pulse 1.6s ease-out infinite;
}

.socket-banner__copy {
  display: flex;
  flex-direction: column;
  gap: 2px;
  line-height: 1.25;
}

.socket-banner__title {
  display: inline-flex;
  align-items: baseline;
  font-weight: 600;
  color: var(--color-text-dark, #170f49);
  font-size: 0.875rem;
}

.socket-banner__subtitle {
  color: var(--color-text-muted, #6b7280);
  font-size: 0.75rem;
}

.socket-banner__ellipsis {
  display: inline-flex;
  margin-left: 2px;
  width: 14px;
}

.socket-banner__ellipsis span {
  width: 3px;
  height: 3px;
  margin: 0 1px;
  align-self: flex-end;
  background: currentColor;
  border-radius: 50%;
  opacity: 0.4;
  animation: socket-banner-dot 1.2s ease-in-out infinite;
}

.socket-banner__ellipsis span:nth-child(2) {
  animation-delay: 0.15s;
}
.socket-banner__ellipsis span:nth-child(3) {
  animation-delay: 0.3s;
}

@keyframes socket-banner-pulse {
  0% {
    transform: scale(0.85);
    opacity: 0.6;
  }
  70% {
    transform: scale(1.25);
    opacity: 0;
  }
  100% {
    transform: scale(1.25);
    opacity: 0;
  }
}

@keyframes socket-banner-dot {
  0%,
  80%,
  100% {
    opacity: 0.25;
    transform: translateY(0);
  }
  40% {
    opacity: 1;
    transform: translateY(-2px);
  }
}

.socket-banner-enter-active,
.socket-banner-leave-active {
  transition:
    opacity 0.22s ease,
    transform 0.22s cubic-bezier(0.22, 1, 0.36, 1);
}

.socket-banner-enter-from,
.socket-banner-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(12px);
}

@media (prefers-reduced-motion: reduce) {
  .socket-banner__pulse,
  .socket-banner__ellipsis span {
    animation: none;
  }
}
</style>
